import { sendEvent } from '@skyscanner-internal/gtm-client';
import {
  getInboundDate,
  getOutboundDate,
} from '@web-platform/shared-universal-redux/src/selectors/search';
import { getUserSearch } from '@web-platform/shared-universal-redux/src/state';
import { getStore } from '@web-platform/shared-universal-redux/src/store';
import {
  getCulture,
  getDateType,
} from '@web-platform/shared-universal-utils-frontend-localisation';

import { getLegLocationCodes } from '../utils/getTrackingMetadata';

import type { EventData } from '@skyscanner-internal/gtm-client';
import type { BananaStore } from '@web-platform/shared-universal-types/src/redux/state/state-shared-types';
import type { SearchPlaceType } from '@web-platform/shared-universal-types/src/requestContext';

type BananaEventNames =
  'banana_day_view_loaded' | 'banana_combined_explore_loaded' | 'banana_homepage_loaded';

type Event = Extract<EventData, { event: BananaEventNames }> & {
  originType?: SearchPlaceType;
  destinationType?: SearchPlaceType;
};

/*
Here we send tracking events to the Google Tag Manager Server container.

https://skyscanner.atlassian.net/wiki/spaces/AMSS/pages/594348175/Google+Tag+Manager+GTM+Validation+-+Design+Review#1.-How-sGTM-works%3F

The order of operations is:

1. gtm-client adds the GTM Web Container via `initTagManager`, and manages setting consent
2. GTM Web Container sits and listens for any changes to the dataLayer
3. sendEvent from gtm-client is used in each codebase per event to push an Event to the dataLayer
4. GTM Web Container has an allowlist of event names to send data received from them to the GTM Server
5. GTM Server has a ‘trigger’ config to specify which event names trigger sending to which third parties

Importantly GTM Server also has logic to manipulate and work with the values sent to it in an Event.

We do the minimum required work client side; if logic can be done on the server we push it there and send only the base data from Banana.

Three examples can be used to indicate what is seen as ideal:

a) `numChildren`. We store children as an array of ages. This is trivial to count as a total, so this is done client side, the complexity of adding custom logic server side is not justified.

b) `outboundDateType`. We have a complex custom util _already_ in the codebase for determining this. Duplicating this util adds extra complexity, so we do this client side before we pass the value.

c) `origin` and `origin_airport`. For Facebook specifically these require two pieces of logic. A custom mapping of our airport codes to values Facebook uses exists. This is pushed server side; there is no value to the traveller shipping this client side. Whether to send `origin_airport` _or_ to send `origin` (which can be a City or Country) depends on if it is a Combined Explore search. We also push this server side; the browser will send all required information to make this decision (origin code, event name) and defer to Facebook how to use it.

We aim to:
- Send the minimum required data
- Send a single event that can satisfy all third party integrations
- Be agnostic to specific third party integrations and push conditional logic to manipulate or use data to GTM Server
*/

export const trackThirdPartyEvent = (eventName: BananaEventNames) => {
  // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
  const store = getStore() as BananaStore;
  const state = store.getState();

  const { currency, market } = getCulture();
  const { adults, cabinClass, child_ages, legs } = getUserSearch(state);
  const { destinationCodeLeg1, originCodeLeg1 } = getLegLocationCodes(legs);
  const outboundDate = getOutboundDate(state);
  const inboundDate = getInboundDate(state);

  const eventData: Event = {
    event: eventName,
    currency,
    market,
    numberOfAdults: adults.toString(),
    numberOfChildren: child_ages.length.toString(),
    cabinClass,
    outboundDate,
    inboundDate,
    pageType: 'banana-gtm',
    originId: originCodeLeg1,
    destinationId: destinationCodeLeg1,
    outboundDateType: getDateType(outboundDate),
    originType: legs[0]?.origin?.type,
    destinationType: legs[0]?.destination?.type,
  };

  sendEvent(eventData);
};
